import type { ProductOptionModel } from '@snowit/types/options';
import type { ProductModel } from '@snowit/types/products';

import { isNewEntity } from '@snowit/constants';
import useFirebase from '@snowit/firebase';
import { SchemaNames } from '@snowit/jsonschemas/__names';
import { useStore } from '@snowit/store';
import omitter from '@snowit/utilities/omitter';

import useSchemas from '../useSchemas';

export default () => {
  const firebase = useFirebase();
  const { setStore, store } = useStore();
  const { writeDocument, deleteDocument } = useSchemas();

  const options = store.options.list.sort(
    (a, b) => (b.updatedAt?.seconds ?? 0) - (a.updatedAt?.seconds ?? 0),
  );

  const getOptionsFromProductUIDS = async (
    ids: string | string[],
  ): Promise<ProductOptionModel[]> => {
    try {
      const productUIDS = Array.isArray(ids) ? ids : [ids];
      const products: ProductModel[] = [];
      for (const id of productUIDS) {
        const doc = await firebase.doc<ProductModel>({ collectionName: 'products', id });
        if (doc) products.push(doc);
      }
      const optionUIDS = products.reduce(
        (memo, product) => [...memo, ...(product.optionUIDS ?? [])],
        [] as string[],
      );
      const ret: ProductOptionModel[] = [];
      for (const id of optionUIDS) {
        const doc = await firebase.doc<ProductOptionModel>({
          collectionName: 'productOptions',
          id: id,
        });
        if (doc) {
          ret.push(doc);
        }
      }
      return ret;
    } catch (error) {
      console.error({ error });
      return [];
    }
  };

  const getOptionsFromUIDS = async (ids: string[]): Promise<ProductOptionModel[]> => {
    try {
      const ret: ProductOptionModel[] = [];
      for (const id of ids) {
        const doc = await firebase.doc<ProductOptionModel>({
          collectionName: 'productOptions',
          id: id,
        });
        if (doc) {
          ret.push(doc);
        }
      }
      return ret;
    } catch (error) {
      console.error({ error });
      return [];
    }
  };

  const getOptions = async (): Promise<ProductOptionModel[]> => {
    try {
      const res = await firebase.query<ProductOptionModel>({
        collectionName: 'productOptions',
        filters: (q) => q.limit(0),
      });

      setStore('options', { list: res });

      return res;
    } catch (error) {
      console.error({ error });
      setStore('options', { list: [] });
      return [];
    } finally {
      setStore('options', { ready: true });
    }
  };

  const writeOption = async (
    optionUID: string | undefined,
    data: Partial<ProductOptionModel>,
  ): Promise<string | undefined> => {
    try {
      const id = await writeDocument({
        modelName: SchemaNames.PRODUCTOPTION,
        attributes: omitter(data),
        id: isNewEntity(optionUID) ? undefined : optionUID,
      });
      return id;
    } catch (error: any) {
      console.error('options_write', { error });
    }
  };

  const deleteOption = async (optionUID: string): Promise<boolean> => {
    try {
      if (isNewEntity(optionUID)) return false;
      await deleteDocument({
        modelName: SchemaNames.PRODUCTOPTION,
        id: optionUID,
      });
      return true;
    } catch (error: any) {
      console.error('deleteOption', { error });
      return false;
    }
  };

  return {
    options,
    ready: store.options.ready,
    getOptions,
    getOptionsFromUIDS,
    getOptionsFromProductUIDS,
    writeOption,
    deleteOption,
  };
};
